"use client";

import clsx from "clsx";
import {useTranslation} from "react-i18next";
import type {CardComponentProps} from "nextstepjs";

export default function DashboardTourCard({
   step,
   currentStep,
   totalSteps,
   nextStep,
   prevStep,
   skipTour,
   arrow,
}: Readonly<CardComponentProps>) {
   const {t} = useTranslation("stats");
   const isFloating = !step.selector;
   const isLastStep = currentStep === totalSteps - 1;
   const progressPercent = ((currentStep + 1) / totalSteps) * 100;

   return (
      <div
         className="bg-panel rounded-lg p-4 shadow-lg"
         style={{
            maxWidth: "32rem",
            minWidth: isFloating ? "24rem" : "16rem",
         }}
      >
         <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-900">{step.title}</h2>
            {step.icon && <span key={currentStep} className="text-2xl">{step.icon}</span>}
         </div>

         <div className="mb-4 text-sm text-700">{step.content}</div>

         <div className="mb-4 bg-200 rounded-full h-2.5">
            <div
               className="bg-gailPrimary h-2.5 rounded-full transition-all"
               style={{width: `${progressPercent}%`}}
            />
         </div>

         <div className="flex justify-between items-center gap-4 text-xs">
            <button
               type="button"
               onClick={prevStep}
               disabled={currentStep === 0}
               className={clsx(
                  "px-3 py-2 font-medium bg-200 text-600 rounded-md cursor-pointer disabled:opacity-50",
                  step.showControls ? "block" : "hidden"
               )}
            >
               {t("tour.locale.back")}
            </button>

            <span className="text-500 whitespace-nowrap">
               {t("tour.locale.progress", {current: currentStep + 1, total: totalSteps})}
            </span>

            <button
               type="button"
               onClick={nextStep}
               className={clsx(
                  "px-3 py-2 font-medium rounded-md cursor-pointer text-button_text_primary",
                  isLastStep ? "bg-green-500" : "bg-gailPrimary",
                  step.showControls ? "block" : "hidden"
               )}
            >
               {isLastStep ? t("tour.locale.last") : t("tour.locale.next")}
            </button>
         </div>

         {arrow}

         {skipTour && currentStep < totalSteps - 1 && (
            <button
               type="button"
               onClick={skipTour}
               className={clsx(
                  "mt-4 text-xs w-full px-3 py-2 font-medium bg-200 text-600 rounded-md cursor-pointer",
                  step.showSkip ? "block" : "hidden"
               )}
            >
               {t("tour.locale.skip")}
            </button>
         )}
      </div>
   );
}
